import React, { useCallback, useEffect, useState } from "react";
import { Platform } from "react-native";
import { getItem } from "@lib/storage";
import {
  checkMultiple,
  RESULTS,
  type Permission,
  type PermissionStatus,
} from "react-native-permissions";
import MovexGeo from "@movexgeo/react-native-movex";
import { trackingEnabled } from "@config";
import PermissionsModal from "./PermissionsModal";
import {
  BATTERY_KEY,
  BATTERY_REQUESTED_KEY,
  loadAllRequiredPermissions,
  type PermissionItem,
} from "./appPermissions";


const isGranted = (status?: PermissionStatus) =>
  status === RESULTS.GRANTED || status === RESULTS.LIMITED;

/**
 * Mounted at app boot — independent of login state. Loads the merged
 * permission list (app perms + Movex SDK perms + battery), checks
 * grant status, and surfaces the PermissionsModal if anything is
 * missing. Used both pre-login (block login screen) and post-login on
 * the homepage (re-prompt if permissions were revoked).
 */
const PermissionsGate: React.FC = () => {
  const [items, setItems] = useState<PermissionItem[] | null>(null);
  const [showPermModal, setShowPermModal] = useState(false);

  useEffect(() => {
    if (Platform.OS !== "android") return;
    if (!trackingEnabled) return;
    let cancelled = false;
    (async () => {
      const list = await loadAllRequiredPermissions();
      if (cancelled) return;
      setItems(list);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const onPermResolved = useCallback((granted: boolean) => {
    setShowPermModal(!granted);
  }, []);

  useEffect(() => {
    if (!items) return;
    async function checkPerms() {
      try {
        const keys = items
          .filter((p) => p.key !== BATTERY_KEY)
          .map((p) => p.key as Permission);
        let result: Record<string, PermissionStatus> = {};
        if (keys.length) {
          result = (await checkMultiple(keys)) as Record<
            string,
            PermissionStatus
          >;
        }
        const hasBattery = items.some((p) => p.key === BATTERY_KEY);
        // catch(() => true): if the API throws, the device doesn't support battery
        // optimization exemption — treat as not applicable and don't block.
        const batteryGranted = hasBattery
          ? await MovexGeo.isIgnoringBatteryOptimizations().catch(() => true)
          : true;
        const batteryEverRequested =
          (await getItem(BATTERY_REQUESTED_KEY).catch(() => null)) === "1";
        const nonBatteryGranted = keys.every((p) => isGranted(result[p]));
        const shouldShow =
          !nonBatteryGranted || (!batteryGranted && !batteryEverRequested);
        if (shouldShow) setShowPermModal(true);
      } catch {
        setShowPermModal(true);
      }
    }
    checkPerms();
  }, [items]);

  if (!items) return null;

  return (
    <PermissionsModal
      visible={showPermModal}
      items={items}
      onResolved={onPermResolved}
    />
  );
};

export default PermissionsGate;
